import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useGetDeviceOutagesQuery, type DeviceStatusHistoryEntry } from '@store/api/deviceApi';
import { dateTime, formatDuration } from '../common/tableCells';

const DAYS = 30;

interface DeviceConnectionHistoryProps {
  deviceId: string;
}

/** Outages of the last DAYS days: one row per outage, rapid reconnects folded into one. */
const DeviceConnectionHistory: React.FC<DeviceConnectionHistoryProps> = ({ deviceId }) => {
  const { data, isLoading, isError, fulfilledTimeStamp } = useGetDeviceOutagesQuery({ deviceId, hours: DAYS * 24 });
  const outages = data?.history ?? [];
  // Ongoing outages and the window are measured from when the data was fetched
  const now = fulfilledTimeStamp ?? 0;
  const windowStart = now - DAYS * 86400 * 1000;
  const end = (entry: DeviceStatusHistoryEntry) =>
    entry.recovered_at ? Date.parse(entry.recovered_at) : now;
  const durationOf = (entry: DeviceStatusHistoryEntry, from = Date.parse(entry.timestamp)) =>
    Math.floor((end(entry) - from) / 1000);
  // Only the part of each outage inside the window counts toward the total
  const downtime = outages.reduce(
    (sum, entry) => sum + durationOf(entry, Math.max(Date.parse(entry.timestamp), windowStart)),
    0
  );
  const reconnects = outages.reduce((sum, entry) => sum + entry.reconnect_count, 0);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" component="h2" gutterBottom>
          Connection History
        </Typography>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 3
            }}>
            <CircularProgress size={24} aria-label="Loading connection history" />
          </Box>
        ) : isError ? (
          <Alert severity="error">Failed to load connection history</Alert>
        ) : outages.length === 0 ? (
          <Typography variant="body2" sx={{
            color: 'text.secondary'
          }}>
            No outages in the last {DAYS} days.
          </Typography>
        ) : (
          <>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mb: 1
              }}>
              {outages.length} {outages.length === 1 ? 'outage' : 'outages'} in the last {DAYS}{' '}
              days, {formatDuration(downtime)} offline in total
              {reconnects > 0 && `, ${reconnects} quick reconnects`}.
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Went offline</TableCell>
                    <TableCell>Back online</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Reconnects</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {outages.map((entry) => (
                    <TableRow key={entry.timestamp}>
                      <TableCell>{dateTime(entry.timestamp)}</TableCell>
                      <TableCell>{entry.recovered_at ? dateTime(entry.recovered_at) : 'Still offline'}</TableCell>
                      <TableCell>{formatDuration(durationOf(entry))}</TableCell>
                      <TableCell>{entry.reconnect_count || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DeviceConnectionHistory;
